import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Users, 
  Building2, 
  MessageSquare, 
  TrendingUp,
  DollarSign,
  Activity,
  MapPin,
  Clock
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  trend?: string;
  trendUp?: boolean;
}

const MetricCard = ({ title, value, icon, trend, trendUp }: MetricCardProps) => (
  <Card>
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <CardTitle className="text-sm font-medium">{title}</CardTitle>
      {icon}
    </CardHeader>
    <CardContent>
      <div className="text-2xl font-bold">{value}</div>
      {trend && (
        <p className={`text-xs ${trendUp ? 'text-green-600' : 'text-red-600'} flex items-center gap-1`}>
          <TrendingUp className="w-3 h-3" />
          {trend}
        </p>
      )}
    </CardContent>
  </Card>
);

const BusinessMetrics = () => {
  const [metrics, setMetrics] = useState({
    totalUsers: 0,
    totalProviders: 0,
    totalListings: 0,
    totalReferrals: 0,
    activeSubscriptions: 0,
    monthlyRevenue: 0,
    avgResponseTime: 0,
    topCounties: [] as { county: string; count: number }[]
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        // Get total users by role
        const { data: profiles } = await supabase
          .from('profiles')
          .select('role');

        const totalUsers = profiles?.length || 0;
        const totalProviders = profiles?.filter(p => p.role === 'provider').length || 0;

        // Get total listings
        const { data: listings } = await supabase
          .from('bed_listings')
          .select('county, clicks, created_at');

        const totalListings = listings?.length || 0;

        // Get total referrals
        const { data: referrals } = await supabase
          .from('referrals')
          .select('created_at, status');

        const totalReferrals = referrals?.length || 0;

        // Get active subscriptions
        const { data: subscribers } = await supabase
          .from('subscribers')
          .select('subscription_tier, payment_status')
          .eq('subscribed', true);

        const activeSubscriptions = subscribers?.length || 0;

        // Calculate monthly revenue (rough estimate)
        const monthlyRevenue = subscribers?.reduce((sum, sub) => {
          if (sub.subscription_tier === 'tier_1') return sum + 100;
          if (sub.subscription_tier === 'tier_2') return sum + 150;
          if (sub.subscription_tier === 'tier_3') return sum + 150;
          return sum;
        }, 0) || 0;

        // Get top counties by listing count
        const countyCounts = listings?.reduce((acc, listing) => {
          acc[listing.county] = (acc[listing.county] || 0) + 1;
          return acc;
        }, {} as Record<string, number>) || {};

        const topCounties = Object.entries(countyCounts)
          .sort(([,a], [,b]) => b - a)
          .slice(0, 5)
          .map(([county, count]) => ({ county, count }));

        // Calculate average response time (mock data for now)
        const avgResponseTime = 24; // hours

        setMetrics({
          totalUsers,
          totalProviders,
          totalListings,
          totalReferrals,
          activeSubscriptions,
          monthlyRevenue,
          avgResponseTime,
          topCounties
        });
      } catch (error) {
        console.error('Error fetching metrics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(8)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="space-y-0 pb-2">
                <div className="h-4 bg-muted rounded w-24"></div>
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-muted rounded w-16"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Business Metrics</h2>
        <p className="text-muted-foreground">
          Key performance indicators for CareKonnect platform
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Total Users"
          value={metrics.totalUsers}
          icon={<Users className="h-4 w-4 text-muted-foreground" />}
          trend="+12% from last month"
          trendUp={true}
        />
        
        <MetricCard
          title="Active Providers"
          value={metrics.totalProviders}
          icon={<Building2 className="h-4 w-4 text-muted-foreground" />}
          trend="+8% from last month"
          trendUp={true}
        />
        
        <MetricCard
          title="Total Listings"
          value={metrics.totalListings}
          icon={<Activity className="h-4 w-4 text-muted-foreground" />}
          trend="+15% from last month"
          trendUp={true}
        />
        
        <MetricCard
          title="Total Referrals"
          value={metrics.totalReferrals}
          icon={<MessageSquare className="h-4 w-4 text-muted-foreground" />}
          trend="+22% from last month"
          trendUp={true}
        />
        
        <MetricCard
          title="Active Subscriptions"
          value={metrics.activeSubscriptions}
          icon={<DollarSign className="h-4 w-4 text-muted-foreground" />}
          trend="+5% from last month"
          trendUp={true}
        />
        
        <MetricCard
          title="Monthly Revenue"
          value={`$${metrics.monthlyRevenue.toLocaleString()}`}
          icon={<TrendingUp className="h-4 w-4 text-muted-foreground" />}
          trend="+18% from last month"
          trendUp={true}
        />
        
        <MetricCard
          title="Avg Response Time"
          value={`${metrics.avgResponseTime}h`}
          icon={<Clock className="h-4 w-4 text-muted-foreground" />}
          trend="-2h from last month"
          trendUp={true}
        />
        
        <MetricCard
          title="Platform Health"
          value="99.9%"
          icon={<Activity className="h-4 w-4 text-muted-foreground" />}
          trend="Uptime"
          trendUp={true}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Top Counties by Listings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {metrics.topCounties.map((county, index) => (
                <div key={county.county} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="w-6 h-6 p-0 flex items-center justify-center">
                      {index + 1}
                    </Badge>
                    <span className="font-medium">{county.county}</span>
                  </div>
                  <span className="text-muted-foreground">{county.count} listings</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>New provider registration</span>
                <span className="text-muted-foreground ml-auto">2 min ago</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span>Referral submitted</span>
                <span className="text-muted-foreground ml-auto">5 min ago</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                <span>Listing updated</span>
                <span className="text-muted-foreground ml-auto">12 min ago</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                <span>Subscription renewed</span>
                <span className="text-muted-foreground ml-auto">18 min ago</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BusinessMetrics;